import React, { useState, useEffect } from "react";
import {
  FaWhatsapp,
  FaArrowRight,
  FaCheckCircle,
  FaStar,
  FaPlus,
  FaMinus,
  FaQuoteLeft,
  FaUtensils,
  FaUsers,
  FaShieldAlt,
} from "react-icons/fa";

// Global layout components
import SalemPageSeo from "../components/SalemPageSeo";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingCta from "../components/FloatingCta";
import ReservationSection from "../components/ReservationSection";

// Asset imagery
import nawabiHeroFeast from "../assets/nawabi_hero_feast.png";
import indianWeddingFeast from "../assets/indian_wedding_feast.png";
import indianBiryaniSpread from "../assets/indian_biryani_spread.png";
import nawabiFoodServing from "../assets/nawabi_food_serving.png";

// Menu category images
import welcomeDrinksImg from "../assets/main-images/Nawabi welcome drinks.jpg.jpeg";
import biryaniImg from "../assets/main-images/Nawabi Briyani.jpg.jpeg";
import riceImg from "../assets/main-images/Nawabi Rice Varieties.jpg.jpeg";
import graviesImg from "../assets/main-images/Nawabi Gravies.jpg.jpeg";
import dryItemsImg from "../assets/main-images/Nawabi Dry Items & BBQ.jpg.jpeg";
import sweetsDessertsImg from "../assets/main-images/Nawabi Sweets & Desserts.jpg.jpeg";

const SalemCateringPage = () => {
  // Slideshow state for hero
  const [currentSlide, setCurrentSlide] = useState(0);
  const heroSlides = [
    { image: nawabiHeroFeast, alt: "Nawabi Caterers Best Catering Services in Salem" },
    { image: indianWeddingFeast, alt: "Salem Wedding Feast by Nawabi Caterers" },
    { image: indianBiryaniSpread, alt: "Salem Dum Biryani Catering Spread" },
    { image: nawabiFoodServing, alt: "Salem Outdoor Catering Staff Serving Guests" },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  // FAQ accordion state
  const [openFaq, setOpenFaq] = useState(0);

  const scrollToSection = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  // 10 Salem FAQs
  const salemFaqs = [
    {
      q: "Which is the best catering service in Salem for my event?",
      a: "Start by matching the caterer to your event. If you want a non-veg or biryani-led spread for a wedding, party or corporate function in and around Salem, Nawabi Caterers is a good fit. The quickest way to know is to send us your date and guest count for a quote.",
    },
    {
      q: "Do you provide wedding catering in Salem?",
      a: "Yes. Weddings are our biggest events. We plan the menu, cook on site and serve your guests, with biryani and non-veg dishes usually at the centre of the meal.",
    },
    {
      q: "Do you provide marriage catering in Salem?",
      a: "Yes. We cater the full run of a marriage, including engagement, nichayathartham, the wedding and the reception, and our staff keep the food ready and the serving on time for each function.",
    },
    {
      q: "Do you provide non-veg catering in Salem?",
      a: "Yes, non-vegetarian food is our specialty. Chicken, mutton, biryani, gravies and rice varieties form the core of our menus, prepared with 100% Halal-certified meat and planned around your event.",
    },
    {
      q: "Do you provide biryani catering in Salem?",
      a: "Yes. Biryani is one of our most requested services, prepared in event quantities and served hot for gatherings of all sizes.",
    },
    {
      q: "Do you offer a corporate catering service in Salem?",
      a: "Yes. We cater office lunches, meetings, launches and staff functions, with an emphasis on punctual, organised and dependable service for a corporate setting.",
    },
    {
      q: "Can I customise the menu?",
      a: "Absolutely. Menus aren't fixed. We plan them with you around the occasion, guest count, preferences and budget. If you have specific requirements, tell us when you enquire.",
    },
    {
      q: "Which locations do you serve?",
      a: "We are based in Salem and cater across the city and nearby areas, as well as across Tamil Nadu, including Chennai and Coimbatore. For other areas, contact us with your location and date and we will confirm availability.",
    },
    {
      q: "Can you cater for large gatherings?",
      a: "Yes. We plan quantities and staff for large weddings, community events and mass functions, so everyone is served properly.",
    },
    {
      q: "How do I request a quote, and how early should I book?",
      a: "Send us your event date, venue, type of occasion and a rough guest count by call, WhatsApp or the enquiry form, and we will put together a quote. Booking earlier is better, especially for weddings and peak-season dates, so your date is secured and there is time to plan the menu.",
    },
  ];

  // 8 Salem Catering Services
  const salemServices = [
    {
      title: "Wedding Catering in Salem",
      description:
        "Weddings are our biggest events, and we treat them that way. We plan multi-course menus, coordinate serving for large guest counts and manage everything from the main biryani to starters, gravies and desserts. Buffet or seated, we set it up to match your hall and your schedule.",
      cta: "See how our wedding catering works",
      targetId: "wedding-catering",
      badge: "Flagship Service",
    },
    {
      title: "Marriage Catering in Salem",
      description:
        "From the nichayathartham and engagement to the reception, a marriage brings a run of functions, each with its own timing. Our marriage catering keeps the food ready at the right moment for every one of them, with serving staff who keep the line moving for a large, mixed crowd.",
      cta: "Plan your marriage catering",
      targetId: "booking-section",
      badge: "Multi-Day Banquets",
    },
    {
      title: "Non-Veg Catering in Salem",
      description:
        "Non-vegetarian food is what we are known for. Chicken and mutton dishes, biryani, rich gravies and rice varieties form the core of our menus. If you want a spread that leans heavily on non-veg, this is where we are most at home.",
      cta: "Explore non-veg catering options",
      targetId: "non-veg-catering",
      badge: "Our Specialty",
    },
    {
      title: "Biryani Catering in Salem",
      description:
        "Biryani is a crowd favourite for a reason: it travels well, serves large numbers and keeps guests happy. We prepare biryani in event quantities without losing the flavour, and pair it with the sides that go with it.",
      cta: "Ask about biryani catering for your event",
      targetId: "biryani-catering",
      badge: "Crowd Favourite",
    },
    {
      title: "Corporate Catering Service in Salem",
      description:
        "For office events, meetings, launches and staff functions, we provide catering that is easy to organise and dependable to serve. Share the headcount and the time slot, and we will plan a menu that works for a corporate setting.",
      cta: "Plan corporate catering",
      targetId: "booking-section",
      badge: "Executive Spread",
    },
    {
      title: "Birthday & Party Catering in Salem",
      description:
        "Birthdays, anniversaries and get-togethers call for food people remember. We put together party menus for small and mid-sized gatherings, at home or at a venue, and keep it as simple or as generous as you like.",
      cta: "Get a party catering quote",
      targetId: "booking-section",
      badge: "Parties & Socials",
    },
    {
      title: "Family & Religious Function Catering",
      description:
        "Naming ceremonies, housewarmings, religious gatherings and family functions each have their own rhythm. We cater these with menus that suit the occasion and serving that respects the setting.",
      cta: "Discuss your function",
      targetId: "booking-section",
      badge: "Intimate Occasions",
    },
    {
      title: "Large-Scale & Mass Event Catering",
      description:
        "For big gatherings, including large weddings, community events and mass functions, the challenge is volume without dropping quality. We plan quantities carefully and staff the event to serve everyone properly.",
      cta: "Talk to us about large events",
      targetId: "booking-section",
      badge: "Up to 5,000+ Guests",
    },
  ];

  // 8 Pillars
  const whyChoosePillars = [
    {
      num: "01",
      title: "We specialise in non-veg and biryani",
      desc: "This isn't a side offering. Non-veg and biryani are our focus, and our menus are built around getting them right.",
    },
    {
      num: "02",
      title: "100% Halal-certified food",
      desc: "All our non-veg dishes are prepared with 100% Halal-certified meat, so you can serve every guest with confidence.",
    },
    {
      num: "03",
      title: "Menus customised to your event",
      desc: "Nothing is fixed. We plan your menu around the occasion, the guest count and your preferences, so it fits your event rather than the other way round.",
    },
    {
      num: "04",
      title: "Outdoor catering, brought to your venue",
      desc: "As an outdoor catering service, we come to you, including halls, farmhouses, homes, grounds or corporate spaces across Salem and Tamil Nadu.",
    },
    {
      num: "05",
      title: "A team that handles the whole event",
      desc: "From setup and cooking to serving, our staff run the catering on the ground, so you can be a host rather than a manager on your big day.",
    },
    {
      num: "06",
      title: "Built for events of different sizes",
      desc: "Whether it is a family lunch or a large wedding, we plan and staff accordingly. The scale changes; the care doesn't.",
    },
    {
      num: "07",
      title: "Freshness and hygiene you can trust",
      desc: "Food is cooked for your event, with care over freshness and clean handling from preparation through to serving, in line with FSSAI standards.",
    },
    {
      num: "08",
      title: "Part of the NonVeeFoods family",
      desc: "Nawabi Caterers is a brand under NonVeeFoods, our non-vegetarian food business, so catering is a natural extension of what we already do.",
    },
  ];

  // 3 Salem Reviews
  const salemReviews = [
    {
      quote:
        "Nawabi Caterers handled our wedding reception at Fairlands for over 800 guests. The mutton biryani was exceptionally fragrant, perfectly cooked, and served piping hot straight through the evening. Their outdoor kitchen setup was clean, professional, and completely stress-free for our family.",
      author: "Karthik & Anitha",
      details: "Wedding Reception · Fairlands, Salem",
      guests: "800 Guests",
    },
    {
      quote:
        "We booked them for our annual corporate meet near Suramangalam. Food was set up exactly on schedule, the presentation suited our executive team, and the tandoori starters were a huge hit. Easily the most dependable corporate catering service in Salem.",
      author: "S. Ramesh, HR Lead",
      details: "Corporate Event · Suramangalam, Salem",
      guests: "Corporate Group",
    },
    {
      quote:
        "We hosted a family function for 150 guests in Hasthampatti. Having 100% Halal-certified non-veg food was our top priority, and Nawabi Caterers delivered on every front. Rich authentic flavours, fresh preparation on-site, and very attentive serving staff.",
      author: "Mohammed Faiz",
      details: "Family Function · Hasthampatti, Salem",
      guests: "150 Guests",
    },
  ];

  // 5 Steps
  const howItWorksSteps = [
    {
      step: "01",
      title: "Tell us about your event",
      desc: "Share the date, venue, type of occasion and a rough guest count.",
    },
    {
      step: "02",
      title: "Discuss the menu and numbers",
      desc: "We talk through food preferences, non-veg and biryani options, and confirm your headcount.",
    },
    {
      step: "03",
      title: "Customise your catering plan",
      desc: "We shape a menu and service setup to fit your event and budget, and share a quote.",
    },
    {
      step: "04",
      title: "We prepare and set up",
      desc: "On the day, our team arrives, sets up and cooks on site at your venue.",
    },
    {
      step: "05",
      title: "We serve your guests",
      desc: "Buffet or served, we handle the serving so every guest is looked after.",
    },
  ];

  // Service Areas
  const salemLocalities = [
    "Fairlands",
    "Suramangalam",
    "Hasthampatti",
    "Ammapet",
    "Junction",
    "Shevapet",
    "Alagapuram",
    "Meyyanur",
    "Gorimedu",
    "Attur",
    "Mettur",
    "Omalur",
    "Sankari",
  ];

  const tnCoverage = [
    "Salem",
    "Namakkal",
    "Erode",
    "Dharmapuri",
    "Krishnagiri",
    "Hosur",
    "Coimbatore",
    "Tirupur",
    "Chennai",
    "Madurai",
    "Trichy",
    "Vellore",
    "Thanjavur",
    "Kumbakonam",
    "Tirunelveli",
    "Ooty",
  ];

  // Menu categories
  const menuCategories = [
    { name: "Biryani, the centrepiece of most of our events", img: biryaniImg },
    { name: "Non-veg main courses, chicken and mutton preparations", img: graviesImg },
    { name: "Starters, to open the meal", img: dryItemsImg },
    { name: "Rice varieties, beyond biryani", img: riceImg },
    { name: "Gravies and curries, to pair with rice and breads", img: graviesImg },
    { name: "Desserts, to finish the meal", img: sweetsDessertsImg },
    { name: "Beverages, to accompany the spread", img: welcomeDrinksImg },
  ];

  // Events we cater
  const eventsList = [
    "Weddings and receptions",
    "Birthday celebrations and anniversaries",
    "Corporate events and office functions",
    "Family functions, housewarmings, naming ceremonies and more",
    "Religious gatherings",
    "School and college events",
    "Public and mass gatherings",
    "Festival celebrations",
  ];

  return (
    <div className="min-h-screen bg-[#051109] text-white selection:bg-[#E8A040] selection:text-[#051109]">
      {/* 1. SEO & JSON-LD Structured Data */}
      <SalemPageSeo />

      {/* 2. Top Header Navigation */}
      <Header />

      <main>
        {/* ── 3. HERO SECTION (Full Height Across All Viewpoints) ── */}
        <section className="relative min-h-screen min-h-[100dvh] flex items-center justify-center overflow-hidden pt-20 pb-12 sm:pt-24 sm:pb-16 lg:py-28">
          {/* Background Slideshow */}
          <div className="absolute inset-0 bg-[#07160c]">
            {heroSlides.map((slide, idx) => (
              <div
                key={idx}
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                  currentSlide === idx ? "opacity-100 scale-100" : "opacity-0 scale-105"
                } transform transition-transform duration-[7000ms]`}
              >
                <img
                  src={slide.image}
                  alt={slide.alt}
                  className="w-full h-full object-cover object-center filter brightness-[0.72] contrast-[1.05]"
                />
              </div>
            ))}

            {/* Ambient Overlays for supreme readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#051109]/95 via-[#051109]/70 to-[#051109]/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#051109] via-transparent to-[#051109]/50" />
            <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#051109] to-transparent pointer-events-none" />
          </div>

          {/* Decorative Glow Orbs */}
          <div className="absolute top-1/4 left-10 w-80 h-80 bg-[#22c55e]/15 rounded-full blur-[130px] pointer-events-none" />
          <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-[#E8A040]/15 rounded-full blur-[140px] pointer-events-none" />

          {/* Container */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 w-full">
            <div className="max-w-2xl mx-auto sm:mx-0 flex flex-col items-center sm:items-start text-center sm:text-left">
              
              {/* Badges Row */}
              <div className="inline-flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-4">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#14532d]/90 border border-[#22c55e]/40 text-[#22c55e] text-[11px] font-semibold tracking-wider uppercase shadow-[0_0_20px_rgba(34,197,94,0.25)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] animate-ping" />
                  100% Halal-Certified Food
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/40 backdrop-blur-sm border border-white/20 text-[#f5ecd8] text-[11px] font-medium tracking-wide">
                  <FaCheckCircle className="text-[#E8A040] w-3 h-3" />
                  Outdoor Catering Specialists · Salem HQ
                </span>
              </div>

              {/* Exact H1 Title */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[44px] font-light text-white leading-[1.2] tracking-tight font-serif mb-4 text-center sm:text-left">
                Best Catering Services in Salem
              </h1>

              {/* Exact Docx Description - Centered & 3 lines clamped on mobile */}
              <p className="hero-desc-clamp line-clamp-3 sm:line-clamp-none text-[#d8ded6] text-sm sm:text-base leading-relaxed mb-8 font-light max-w-xl text-center sm:text-left mx-auto sm:mx-0">
                Looking for the best catering services in Salem for a wedding, a corporate event or a family celebration? Nawabi Caterers is an outdoor catering brand from the NonVeeFoods family, and non-veg food and biryani are exactly what we do. From an intimate house function to a large wedding reception, we plan the menu, cook fresh on site and serve your guests, so you get to enjoy the day instead of running it. Tell us your date, your headcount and the food your guests love, and we will take care of the rest.
              </p>

              {/* Action Buttons Row */}
              <div className="flex flex-row items-center justify-center sm:justify-start gap-2 sm:gap-4 mb-8 w-full max-w-md sm:max-w-none">
                <a
                  href="#booking-section"
                  onClick={scrollToSection("booking-section")}
                  className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 sm:gap-2.5 px-3 sm:px-7 py-2.5 sm:py-3.5 bg-gradient-to-r from-[#D4AF37] via-[#E8A040] to-[#F5C070] text-[#0a1a0f] font-bold text-[10px] xs:text-[11px] sm:text-xs tracking-normal sm:tracking-[0.14em] uppercase rounded-full shadow-[0_0_25px_rgba(232,160,64,0.35)] hover:shadow-[0_0_40px_rgba(232,160,64,0.55)] hover:scale-105 transition-all duration-300 group text-center shrink-0 min-w-0 whitespace-nowrap"
                >
                  <span>Request a Catering Quote</span>
                  <FaArrowRight className="w-2.5 h-2.5 sm:w-3 sm:h-3 transition-transform duration-300 group-hover:translate-x-1 shrink-0" />
                </a>

                <a
                  href="https://wa.me/919150299336?text=Hi%20Nawabi%20Caterers,%20I%20would%20like%20to%20discuss%20catering%20services%20in%20Salem%20for%20my%20event."
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 sm:gap-2.5 px-3 sm:px-6 py-2.5 sm:py-3.5 bg-[#0d2a17]/90 hover:bg-[#143e23] border border-[#22c55e]/50 text-white text-[10px] xs:text-[11px] sm:text-xs font-semibold tracking-normal sm:tracking-wide rounded-full transition-all duration-300 hover:scale-105 shadow-lg group text-center shrink-0 min-w-0 whitespace-nowrap"
                >
                  <FaWhatsapp className="text-[#25D366] w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform group-hover:scale-110 shrink-0" />
                  <span>Call or WhatsApp Us</span>
                </a>
              </div>

              {/* 3 Metrics Banner */}
              <div className="pt-6 border-t border-white/15 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 w-full">
                {[
                  { icon: FaShieldAlt, label: "100% Halal-Certified Food", sub: "Strict verified sourcing" },
                  { icon: FaUsers, label: "50 to 5,000+ Guests", sub: "Built for all scales" },
                  { icon: FaCheckCircle, label: "FSSAI Food-Safety Standards", sub: "Cooked fresh on site" },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-center sm:justify-start gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/10 text-center sm:text-left"
                  >
                    <item.icon className="text-[#E8A040] w-4 h-4 shrink-0" />
                    <div className="text-center sm:text-left">
                      <div className="text-white text-xs font-semibold leading-tight">{item.label}</div>
                      <div className="text-white/60 text-[10px] mt-0.5">{item.sub}</div>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </section>

        {/* ── 4. WHY CHOOSE NAWABI CATERERS IN SALEM ── */}
        <section className="bg-[#f8f5ee] text-[#1c2e22] py-20 lg:py-28 relative">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Section Header */}
            <div className="text-center max-w-3xl mx-auto mb-14">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#1b4329]/10 text-[#1b4329] text-xs font-bold tracking-[0.25em] uppercase mb-4 border border-[#1b4329]/20">
                Salem Catering Excellence
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light font-serif leading-tight text-[#0a1a0f] mb-4">
                Why Choose Nawabi Caterers for Catering Services in Salem
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-[#E8A040] mx-auto rounded-full" />
            </div>

            {/* 4 Cards Grid (2x2 Layout matching IntroSection design) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-12">
              
              {/* Card 1 */}
              <div className="group bg-white rounded-2xl p-7 lg:p-9 border border-[#e3dcd1] shadow-sm hover:bg-[#092212] hover:border-[#092212] transition-all duration-300">
                <span className="font-serif font-bold text-lg lg:text-xl text-[#b8731b] group-hover:text-[#E8A040] transition-colors duration-300 block mb-3">
                  01. Taste, Dependability & Budget
                </span>
                <p className="text-[#3a473e] text-sm lg:text-[15px] leading-relaxed group-hover:text-white/80 transition-colors duration-300">
                  When you search for catering services in Salem, you are usually after three things: food that tastes great, a team that shows up and runs the day smoothly, and a menu that fits your event and your budget. That is exactly what we are built around.
                </p>
              </div>

              {/* Card 2 */}
              <div className="group bg-white rounded-2xl p-7 lg:p-9 border border-[#e3dcd1] shadow-sm hover:bg-[#092212] hover:border-[#092212] transition-all duration-300">
                <span className="font-serif font-bold text-lg lg:text-xl text-[#b8731b] group-hover:text-[#E8A040] transition-colors duration-300 block mb-3">
                  02. Salem Outdoor Kitchen & Crew
                </span>
                <p className="text-[#3a473e] text-sm lg:text-[15px] leading-relaxed group-hover:text-white/80 transition-colors duration-300">
                  We are an outdoor catering service, which means we bring the kitchen and the crew to your venue, including wedding halls, farmhouses, community grounds, homes or corporate spaces across Salem. Our strength is non-veg catering in Salem, with biryani-led menus cooked in the quantities your gathering needs and served hot.
                </p>
              </div>

              {/* Card 3 */}
              <div className="group bg-white rounded-2xl p-7 lg:p-9 border border-[#e3dcd1] shadow-sm hover:bg-[#092212] hover:border-[#092212] transition-all duration-300">
                <span className="font-serif font-bold text-lg lg:text-xl text-[#b8731b] group-hover:text-[#E8A040] transition-colors duration-300 block mb-3">
                  03. Customised To Every Event
                </span>
                <p className="text-[#3a473e] text-sm lg:text-[15px] leading-relaxed group-hover:text-white/80 transition-colors duration-300">
                  Every event is different, so nothing is off-the-shelf. We sit down with you, understand the occasion and the guest count, and build a customised menu around it. Whether it is a wedding for several hundred guests or a birthday lunch at home, the approach is the same: get the food right, and get the service right.
                </p>
              </div>

              {/* Card 4 */}
              <div className="group bg-white rounded-2xl p-7 lg:p-9 border border-[#e3dcd1] shadow-sm hover:bg-[#092212] hover:border-[#092212] transition-all duration-300">
                <span className="font-serif font-bold text-lg lg:text-xl text-[#b8731b] group-hover:text-[#E8A040] transition-colors duration-300 block mb-3">
                  04. Salem Base & Tamil Nadu Wide
                </span>
                <p className="text-[#3a473e] text-sm lg:text-[15px] leading-relaxed group-hover:text-white/80 transition-colors duration-300">
                  We are based in Salem and also cater across Tamil Nadu, including Chennai and Coimbatore. If your venue is a little further out, just ask and we will confirm your date.
                </p>
              </div>

            </div>

            {/* 3 Metric Pills */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 pt-6 border-t border-[#e3dcd1]">
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#1b4329]">
                <FaCheckCircle className="text-[#E8A040]" /> 100% Halal-Certified Food
              </span>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#1b4329]">
                <FaCheckCircle className="text-[#E8A040]" /> 50 to 5,000+ Guests Per Event
              </span>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#1b4329]">
                <FaCheckCircle className="text-[#E8A040]" /> FSSAI Food-Safety Standards
              </span>
            </div>

          </div>
        </section>

        {/* ── 5. OUR CATERING SERVICES IN SALEM (8 Cards) ── */}
        <section className="bg-[#051109] py-24 relative overflow-hidden" id="salem-services">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            
            {/* Header */}
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#1b4329] border border-[#2d6a42] text-[#22c55e] text-xs font-semibold tracking-wider uppercase mb-4">
                Full-Service Catering
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white font-light mb-4">
                Our Catering Services in Salem
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-[#E8A040] mx-auto rounded-full mb-5" />
              <p className="text-[#9ea8a2] text-sm sm:text-base leading-relaxed">
                Each event gets a menu and a service plan suited to it.
              </p>
            </div>

            {/* 8 Grid Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {salemServices.map((svc, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl p-6 bg-gradient-to-br from-[#0c2014] to-[#07160c] border border-white/10 hover:border-[#E8A040]/40 transition-all duration-300 flex flex-col justify-between group shadow-lg"
                >
                  <div>
                    <span className="text-[10px] font-bold tracking-widest uppercase text-[#FFD580] bg-[#E8A040]/15 px-2.5 py-1 rounded-full border border-[#E8A040]/30 inline-block mb-3">
                      {svc.badge}
                    </span>
                    <h3 className="text-xl font-serif font-normal text-white mb-3 group-hover:text-[#FFD580] transition-colors">
                      {svc.title}
                    </h3>
                    <p className="text-white/70 text-xs sm:text-sm leading-relaxed mb-6 font-light">
                      {svc.description}
                    </p>
                  </div>

                  <a
                    href={`#${svc.targetId}`}
                    onClick={scrollToSection(svc.targetId)}
                    className="inline-flex items-center gap-2 text-xs font-semibold text-[#E8A040] hover:text-[#FFD580] transition-colors pt-3 border-t border-white/10"
                  >
                    <span>{svc.cta}</span>
                  </a>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* ── 6. SPOTLIGHT 1: WEDDING CATERING IN SALEM ── */}
        <section className="bg-[#091b10] py-24 border-y border-white/10 relative overflow-hidden" id="wedding-catering">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              
              <div className="lg:col-span-7 space-y-6">
                <span className="inline-block px-3.5 py-1 rounded-full bg-[#E8A040]/15 border border-[#E8A040]/30 text-[#FFD580] text-xs font-semibold tracking-wider uppercase">
                  Salem Weddings & Receptions
                </span>
                
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white font-light leading-tight">
                  Planning a Wedding in Salem? <br />
                  <span className="italic text-[#FFD580]">Here's How Our Catering Works</span>
                </h2>

                <p className="text-white/80 text-sm sm:text-base leading-relaxed font-light">
                  A wedding is the one event where the food gets remembered and talked about long after the day. That is why our wedding catering in Salem starts with a proper conversation, not a fixed menu.
                </p>

                <div className="space-y-4 pt-2">
                  {[
                    {
                      title: "Menu planning built around your wedding",
                      text: "We shape a menu that fits the style of your wedding, your family's preferences and your budget. Biryani and non-veg dishes usually anchor the spread, with starters, rice varieties, gravies and desserts filling it out. Want something specific? Tell us and we will plan for it.",
                    },
                    {
                      title: "Cooking for the right guest count",
                      text: "Catering for a hundred guests and catering for a thousand are different jobs. We plan quantities around your confirmed headcount so there is enough for everyone, with the comfortable buffer a wedding needs.",
                    },
                    {
                      title: "Buffet and serving arrangements",
                      text: "Depending on your hall and the flow of the event, we set up buffet counters or arrange served meals, and advise on what works best for your venue and guest movement.",
                    },
                    {
                      title: "Service staff and event coordination",
                      text: "Our team handles the on-site setup, cooking and serving, and works to your timeline so the food is ready when your guests are. You focus on the wedding; we will run the catering.",
                    },
                  ].map((p, i) => (
                    <div key={i} className="p-4 rounded-xl bg-white/[0.04] border border-white/10">
                      <h4 className="text-sm font-semibold text-[#FFD580] mb-1 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#E8A040]" />
                        {p.title}
                      </h4>
                      <p className="text-white/70 text-xs sm:text-sm leading-relaxed font-light pl-3.5">
                        {p.text}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="pt-4">
                  <a
                    href="#booking-section"
                    onClick={scrollToSection("booking-section")}
                    className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#E8A040] text-black font-semibold text-xs tracking-wider uppercase shadow-lg hover:brightness-110 transition-all"
                  >
                    <span>Planning a wedding? Request a wedding catering quote</span>
                    <FaArrowRight className="text-[10px]" />
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5 relative">
                <div className="rounded-2xl overflow-hidden border-2 border-[#D4AF37]/30 shadow-2xl">
                  <img
                    src={indianWeddingFeast}
                    alt="Salem Wedding Catering by Nawabi Caterers"
                    className="w-full h-[460px] object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 p-4 rounded-xl bg-[#07160c]/95 border border-[#D4AF37]/40 shadow-xl hidden sm:block">
                  <div className="text-xs font-bold text-[#FFD580] uppercase">Salem Marriage Specialists</div>
                  <div className="text-xs text-white/80">Nichayathartham · Kalyanam · Reception</div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ── 7. SPOTLIGHT 2: NON-VEG CATERING IN SALEM ── */}
        <section className="bg-[#051109] py-24 relative overflow-hidden" id="non-veg-catering">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              
              <div className="lg:col-span-5 order-2 lg:order-1 relative">
                <div className="rounded-2xl overflow-hidden border-2 border-[#D4AF37]/30 shadow-2xl">
                  <img
                    src={nawabiFoodServing}
                    alt="Salem Non-Veg Catering by Nawabi Caterers"
                    className="w-full h-[440px] object-cover"
                  />
                </div>
              </div>

              <div className="lg:col-span-7 order-1 lg:order-2 space-y-6">
                <span className="inline-block px-3.5 py-1 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/30 text-[#22c55e] text-xs font-semibold tracking-wider uppercase">
                  100% Halal Verified
                </span>
                
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white font-light leading-tight">
                  Non-Veg Catering in Salem, <br />
                  <span className="italic text-[#FFD580]">Done Properly</span>
                </h2>

                <p className="text-white/80 text-sm sm:text-base leading-relaxed font-light">
                  Non-vegetarian catering is the heart of what Nawabi Caterers does. If your event calls for a non-veg spread that satisfies, this is our specialty. Our menus are typically built around:
                </p>

                <ul className="space-y-3 pt-1">
                  {[
                    "Biryani, the centrepiece of most of our events",
                    "Chicken dishes, a range of preparations to suit the occasion",
                    "Mutton dishes, for a richer, festive spread",
                    "Gravies and curries, to pair with rice and breads",
                    "Rice varieties beyond biryani, to round out the meal",
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-white/90">
                      <span className="w-2 h-2 rounded-full bg-[#E8A040] shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <p className="text-white/70 text-sm leading-relaxed font-light">
                  The exact dishes are planned with you, so your menu matches the event, whether it is a wedding, a corporate lunch, a birthday or a family function. If you have particular preferences for how the food is prepared, tell us when you enquire. Live counters can also be arranged for events that call for them.
                </p>

                <div className="pt-3">
                  <a
                    href="#booking-section"
                    onClick={scrollToSection("booking-section")}
                    className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#E8A040] text-black font-semibold text-xs tracking-wider uppercase shadow-lg hover:brightness-110 transition-all"
                  >
                    <span>Build your non-veg menu</span>
                    <FaArrowRight className="text-[10px]" />
                  </a>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ── 8. SPOTLIGHT 3: BIRYANI CATERING IN SALEM ── */}
        <section className="bg-[#091b10] py-24 border-t border-white/10 relative overflow-hidden" id="biryani-catering">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              
              <div className="lg:col-span-7 space-y-6">
                <span className="inline-block px-3.5 py-1 rounded-full bg-[#E8A040]/15 border border-[#E8A040]/30 text-[#FFD580] text-xs font-semibold tracking-wider uppercase">
                  Signature Specialty
                </span>
                
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white font-light leading-tight">
                  Biryani Catering in Salem <br />
                  <span className="italic text-[#FFD580]">for Every Occasion</span>
                </h2>

                <p className="text-white/80 text-sm sm:text-base leading-relaxed font-light">
                  Ask any host what keeps guests happy, and biryani is near the top of the list. It is filling, it is festive, and it works for almost any occasion, which is why biryani catering is one of our most requested services in Salem. It suits just about every event we cater:
                </p>

                <ul className="space-y-3 pt-1">
                  {[
                    "Weddings and receptions, as the main dish of the meal",
                    "Birthdays and parties, an easy, reliable crowd-pleaser",
                    "Corporate events, when you need to feed a group well without fuss",
                    "Family and religious functions, as a shared centrepiece",
                    "Large gatherings, where it serves big numbers dependably",
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-white/90">
                      <span className="w-2 h-2 rounded-full bg-[#E8A040] shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <p className="text-white/70 text-sm leading-relaxed font-light">
                  What matters with event biryani is getting three things right: preparation, flavour and quantity. We cook in event-sized batches while keeping the taste consistent, plan portions around your headcount, and serve it hot so it reaches your guests at its best.
                </p>

                <div className="pt-3">
                  <a
                    href="#booking-section"
                    onClick={scrollToSection("booking-section")}
                    className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#E8A040] text-black font-semibold text-xs tracking-wider uppercase shadow-lg hover:brightness-110 transition-all"
                  >
                    <span>Ask about biryani catering for your date</span>
                    <FaArrowRight className="text-[10px]" />
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5 relative">
                <div className="rounded-2xl overflow-hidden border-2 border-[#D4AF37]/30 shadow-2xl">
                  <img
                    src={indianBiryaniSpread}
                    alt="Authentic Salem Dum Biryani Catering Spread"
                    className="w-full h-[440px] object-cover"
                  />
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ── 9. WHY HOSTS IN SALEM CHOOSE NAWABI CATERERS (8 Pillars) ── */}
        <section className="bg-[#051109] py-24 relative overflow-hidden" id="why-choose-salem">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#1b4329] text-[#22c55e] text-xs font-semibold tracking-wider uppercase mb-4 border border-[#2d6a42]">
                Our Commitments
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white font-light mb-4">
                Why Hosts in Salem Choose Nawabi Caterers
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-[#E8A040] mx-auto rounded-full" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyChoosePillars.map((p, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl p-6 bg-white/[0.03] border border-white/10 hover:border-[#E8A040]/40 hover:bg-[#092212] transition-all duration-300 group"
                >
                  <span className="text-2xl font-serif font-bold text-[#b8731b] group-hover:text-[#FFD580] transition-colors block mb-3">
                    {p.num}
                  </span>
                  <h3 className="text-base font-semibold text-white mb-2 leading-snug">
                    {p.title}
                  </h3>
                  <p className="text-white/65 text-xs sm:text-sm leading-relaxed font-light">
                    {p.desc}
                  </p>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* ── 10. WHAT OUR CLIENTS SAY (Real Salem Reviews) ── */}
        <section className="bg-[#f8f5ee] text-[#1c2e22] py-24 relative overflow-hidden" id="salem-testimonials">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#1b4329]/10 text-[#1b4329] text-xs font-bold tracking-[0.25em] uppercase mb-4 border border-[#1b4329]/20">
                In Our Clients Words
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light text-[#0a1a0f] mb-4">
                What Our Clients Say
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-[#E8A040] mx-auto rounded-full mb-6" />
              <p className="text-[#4b5563] text-sm sm:text-base leading-relaxed">
                Nawabi Caterers is part of the NonVeeFoods family, a homegrown Salem food group that has built its name on the city's dining scene. Along the way, the group's food and service have earned genuine local recognition.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {salemReviews.map((rev, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl p-8 bg-white border border-[#e3dcd1] shadow-md flex flex-col justify-between hover:shadow-xl transition-all duration-300"
                >
                  <div>
                    {/* Stars */}
                    <div className="flex items-center gap-1 text-[#E8A040] mb-4">
                      {[...Array(5)].map((_, i) => (
                        <FaStar key={i} className="text-sm" />
                      ))}
                    </div>

                    <FaQuoteLeft className="text-[#1b4329]/20 text-3xl mb-4" />

                    <p className="text-[#2b3930] text-sm leading-relaxed mb-6 font-normal">
                      "{rev.quote}"
                    </p>
                  </div>

                  <div className="pt-4 border-t border-[#e3dcd1]">
                    <div className="font-serif font-bold text-base text-[#0a1a0f]">
                      {rev.author}
                    </div>
                    <div className="text-xs text-[#6b7280] font-medium mt-0.5">
                      {rev.details}
                    </div>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* ── 11. HOW OUR CATERING SERVICE WORKS (5-Step Numbered Flow) ── */}
        <section className="bg-[#051109] py-24 relative overflow-hidden" id="how-it-works">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#1b4329] text-[#22c55e] text-xs font-semibold tracking-wider uppercase mb-4 border border-[#2d6a42]">
                Simple & Seamless Process
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white font-light mb-4">
                How Our Catering Service Works
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-[#E8A040] mx-auto rounded-full" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {howItWorksSteps.map((s, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl p-6 bg-white/[0.03] border border-white/10 hover:border-[#E8A040]/40 transition-all flex flex-col justify-between"
                >
                  <div>
                    <span className="text-3xl font-serif font-bold text-[#E8A040] block mb-3">
                      {s.step}
                    </span>
                    <h3 className="text-base font-semibold text-white mb-2 leading-snug">
                      {s.title}
                    </h3>
                    <p className="text-white/65 text-xs sm:text-sm leading-relaxed font-light">
                      {s.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* ── 12. WE SERVE SALEM AND ACROSS TAMIL NADU ── */}
        <section className="bg-[#091b10] py-20 border-y border-white/10 relative" id="salem-service-areas">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#E8A040]/15 text-[#FFD580] text-xs font-semibold tracking-wider uppercase mb-4 border border-[#E8A040]/30">
              Coverage & Base
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif text-white font-light mb-3">
              We Serve Salem and Across Tamil Nadu
            </h2>
            <p className="text-white/70 text-sm max-w-2xl mx-auto mb-8 font-light">
              Based in Salem, we cater across the city and the wider region and travel across Tamil Nadu for events.
            </p>

            {/* Salem Localities */}
            <div className="mb-6">
              <div className="text-xs uppercase tracking-widest text-[#E8A040] font-semibold mb-3">
                Key Salem City Hubs & Localities
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {salemLocalities.map((loc, i) => (
                  <span
                    key={i}
                    className="px-3.5 py-1.5 rounded-full bg-white/[0.06] border border-white/15 text-white text-xs font-medium hover:border-[#E8A040] transition-colors"
                  >
                    📍 {loc}
                  </span>
                ))}
              </div>
            </div>

            {/* Tamil Nadu Wide Cities */}
            <div>
              <div className="text-xs uppercase tracking-widest text-[#FFD580] font-semibold mb-3">
                Tamil Nadu Wide Event Coverage
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-6">
                {tnCoverage.map((city, i) => (
                  <span
                    key={i}
                    className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                      city === "Salem"
                        ? "bg-[#E8A040] text-black shadow-md font-bold"
                        : "bg-white/[0.04] border border-white/10 text-white/85"
                    }`}
                  >
                    {city}
                  </span>
                ))}
              </div>
            </div>

            <p className="text-[#9ea8a2] text-xs sm:text-sm font-light italic">
              We travel beyond this list too. Enquire for outstation event catering availability.
            </p>

          </div>
        </section>

        {/* ── 13. A LOOK AT OUR CATERING MENU ── */}
        <section className="bg-[#051109] py-24 relative overflow-hidden" id="salem-menu-preview">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#1b4329] text-[#22c55e] text-xs font-semibold tracking-wider uppercase mb-4 border border-[#2d6a42]">
                Culinary Highlights
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white font-light mb-4">
                A Look at Our Catering Menu
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-[#E8A040] mx-auto rounded-full mb-5" />
              <p className="text-[#9ea8a2] text-sm sm:text-base leading-relaxed">
                Our menus are put together for each event, but they are generally built from these categories. Your final menu is planned with you when you enquire.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {menuCategories.map((cat, i) => (
                <div
                  key={i}
                  className="group rounded-2xl overflow-hidden bg-white/[0.03] border border-white/10 hover:border-[#E8A040]/50 transition-all duration-300"
                >
                  <div className="h-44 overflow-hidden relative">
                    <img
                      src={cat.img}
                      alt={cat.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-white group-hover:text-[#FFD580] transition-colors leading-snug">
                      {cat.name}
                    </h3>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 rounded-2xl bg-[#0d2a17]/80 border border-[#22c55e]/30 text-center max-w-2xl mx-auto">
              <p className="text-white/90 text-sm mb-4 font-light">
                Prefer a live counter or a particular dish at your event? Mention it when you enquire and we will tell you what we can arrange.
              </p>
              <a
                href="#booking-section"
                onClick={scrollToSection("booking-section")}
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#FFD580] hover:text-white transition-colors"
              >
                <span>Discuss your menu with us</span>
                <FaArrowRight className="text-[10px]" />
              </a>
            </div>

          </div>
        </section>

        {/* ── 14. EVENTS WE CATER ── */}
        <section className="bg-[#091b10] py-20 border-t border-white/10" id="events-we-cater">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#E8A040]/15 text-[#FFD580] text-xs font-semibold tracking-wider uppercase mb-4 border border-[#E8A040]/30">
              Celebrations of Every Scale
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white font-light mb-8">
              Events We Cater in Salem
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 text-left">
              {eventsList.map((evt, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-xl bg-white/[0.04] border border-white/10 flex items-center gap-3 hover:border-[#E8A040]/40 transition-colors"
                >
                  <FaUtensils className="text-[#E8A040] text-xs shrink-0" />
                  <span className="text-white text-xs sm:text-sm font-medium">{evt}</span>
                </div>
              ))}
            </div>

            <p className="text-white/70 text-sm max-w-xl mx-auto mb-6 font-light">
              If your event isn't on this list, ask. If it involves feeding a crowd, we can probably help.
            </p>

            <a
              href="#booking-section"
              onClick={scrollToSection("booking-section")}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#E8A040] text-black font-semibold text-xs tracking-wider uppercase shadow-lg hover:brightness-110 transition-all"
            >
              <span>Book your feast, send an enquiry</span>
              <FaArrowRight className="text-[10px]" />
            </a>

          </div>
        </section>

        {/* ── 15. BOOK YOUR ROYAL FEAST ENQUIRY FORM (Interactive Menu & Quote Builder) ── */}
        <ReservationSection />

        {/* ── 16. FREQUENTLY ASKED QUESTIONS (10 Salem FAQs) ── */}
        <section className="bg-[#f8f5ee] text-[#1c2e22] py-24 relative overflow-hidden" id="faq">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#1b4329]/10 text-[#1b4329] text-xs font-bold tracking-[0.25em] uppercase mb-4 border border-[#1b4329]/20">
                Good to Know
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light text-[#0a1a0f] mb-4">
                Frequently Asked Questions
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-[#E8A040] mx-auto rounded-full mb-6" />
              <p className="text-[#4b5563] text-sm sm:text-base leading-relaxed">
                Everything you need to know about booking Nawabi Caterers for your event in Salem.
              </p>
            </div>

            <div className="space-y-4">
              {salemFaqs.map((faq, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div
                    key={idx}
                    className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                      isOpen
                        ? "bg-white border-[#E8A040] shadow-md"
                        : "bg-white/80 border-[#e3dcd1] hover:border-[#b8731b]/50"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                      className="w-full p-6 text-left flex items-center justify-between gap-4 cursor-pointer"
                      aria-expanded={isOpen}
                    >
                      <span className="font-semibold text-base sm:text-lg text-[#0a1a0f] pr-4 leading-snug">
                        {faq.q}
                      </span>
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                          isOpen
                            ? "bg-[#E8A040] text-black"
                            : "bg-[#1b4329]/10 text-[#1b4329]"
                        }`}
                      >
                        {isOpen ? <FaMinus className="w-3 h-3" /> : <FaPlus className="w-3 h-3" />}
                      </div>
                    </button>

                    {isOpen && (
                      <div className="px-6 pb-6 pt-2 border-t border-[#f0ebe0]">
                        <p className="text-[#4b5563] text-sm sm:text-base leading-relaxed">
                          {faq.a}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

          </div>
        </section>

        {/* ── 17. FINAL CALL TO ACTION ── */}
        <section className="bg-[#051109] py-24 relative overflow-hidden border-t border-white/10 text-center">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#1b4329] text-[#22c55e] text-xs font-semibold tracking-wider uppercase mb-4 border border-[#2d6a42]">
              Let's Talk Food
            </span>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white font-light leading-tight mb-6">
              Ready to Plan Your Event in Salem? <br />
              <span className="italic text-[#FFD580]">Let's Talk Food</span>
            </h2>

            <p className="text-white/80 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto mb-10 font-light">
              Whether it is a wedding, a birthday, a corporate function or a family celebration in Salem, Nawabi Caterers is ready to plan a non-veg and biryani spread your guests will remember. Tell us about your event, share your guest count, and we will build a menu and a quote around it.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <a
                href="#booking-section"
                onClick={scrollToSection("booking-section")}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full bg-gradient-to-r from-[#D4AF37] via-[#E8A040] to-[#F5C070] text-[#0a1a0f] font-bold text-[11px] sm:text-xs uppercase tracking-normal sm:tracking-[0.14em] shadow-[0_0_30px_rgba(232,160,64,0.35)] hover:shadow-[0_0_45px_rgba(232,160,64,0.55)] hover:scale-105 transition-all text-center cursor-pointer whitespace-nowrap"
              >
                Request a Catering Quote
              </a>

              <a
                href="https://wa.me/919150299336?text=Hi%20Nawabi%20Caterers,%20I%20would%20like%20to%20discuss%20catering%20services%20in%20Salem."
                target="_blank"
                rel="noreferrer"
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full bg-[#0d2a17] hover:bg-[#143e23] border border-[#22c55e]/50 text-white font-semibold text-[11px] sm:text-xs uppercase tracking-normal sm:tracking-wide transition-all hover:scale-105 flex items-center justify-center gap-2 text-center whitespace-nowrap"
              >
                <FaWhatsapp className="text-[#25D366] text-sm sm:text-base" />
                <span>Call or WhatsApp Us</span>
              </a>
            </div>

          </div>
        </section>
      </main>

      {/* 18. Footer & Floating CTA */}
      <Footer />
      <FloatingCta />
    </div>
  );
};

export default SalemCateringPage;
